import AxiosService from '@core/services/app/axiosService';
import authHeader from '@core/services/app/authHeader';
import NavigationService from '@core/services/utils/appNavigationService';
import appConsoleService from '@core/services/utils/appConsoleService';
import appNotificationService from '@core/services/utils/appNotificationService';
import AppActivityService from '@core/services/app/appActivityService';
import {useAppMenuShortcutStore, MAX_PINS, MAX_RECENTS} from '@core/stores/app/appMenuShortcutStore';
import {useAppMenuStore} from '@core/stores/app/appMenuStore';

/**
 * Module Raccourcis (tiroir, toutes marques) :
 *  - épinglés : persistés serveur (/appUserMenuPin/list|save), suivent l'utilisateur ;
 *  - récents  : derniers écrans consultés, lus dans le journal d'activité
 *               (/appActivity/recent), donc communs à tous les postes.
 * Les entrées sont des `menuKey` ; épingler se fait depuis l'en-tête du module
 * (appHeader), la gestion depuis la page Raccourcis (appShortcuts). Singleton.
 */
class AppMenuShortcutService {

  getStore() {
    if (!this.store) {
      this.store = useAppMenuShortcutStore()
    }
    return this.store
  }

  /** Chargement au login : épinglés (les récents sont rechargés par la page Raccourcis). */
  async load() {
    try {
      const res = await AxiosService.post('/appUserMenuPin/list', {}, {headers: authHeader()})
      this.getStore().setPins(res.data?.menuKeys || [])
    } catch (error) {
      appConsoleService.error('appMenuShortcutService', error)
    }
  }

  // =========================================================
  // Épinglés
  // =========================================================

  async savePins(keys) {
    const store = this.getStore()
    const previous = store.pins
    store.setPins(keys)   // optimiste : l'UI suit immédiatement
    try {
      const res = await AxiosService.post('/appUserMenuPin/save', {menuKeys: store.pins}, {headers: authHeader()})
      store.setPins(res.data?.menuKeys || [])
    } catch (error) {
      store.setPins(previous)
      appConsoleService.error('appMenuShortcutService', error)
      appNotificationService.pushError(error)
    }
  }

  /** @returns {boolean} false si la limite est atteinte */
  async togglePin(menuKey) {
    if (!menuKey) return false
    // Garde-fou : une route paramétrée (détail projet / employé…) n'est pas épinglable
    const item = useAppMenuStore().retrieveItem(menuKey)
    if (item && !this.getStore().isShortcutable(item)) return false
    const pins = this.getStore().pins
    if (pins.includes(menuKey)) {
      await this.savePins(pins.filter(k => k !== menuKey))
      return true
    }
    if (pins.length >= MAX_PINS) return false
    await this.savePins([...pins, menuKey])
    return true
  }

  unpin(menuKey) {
    return this.savePins(this.getStore().pins.filter(k => k !== menuKey))
  }

  /** Déplace `menuKey` à la place de `targetKey` (glisser-déposer). */
  movePin(menuKey, targetKey) {
    const pins = [...this.getStore().pins]
    const from = pins.indexOf(menuKey)
    const to = pins.indexOf(targetKey)
    if (from < 0 || to < 0 || from === to) return
    pins.splice(from, 1)
    pins.splice(to, 0, menuKey)
    return this.savePins(pins)
  }

  // =========================================================
  // Récents (journal d'activité serveur : consultations d'écrans)
  // =========================================================

  async loadRecents() {
    try {
      this.getStore().setRecents(await AppActivityService.recent(MAX_RECENTS * 2))
    } catch (error) {
      appConsoleService.error('appMenuShortcutService', error)
    }
  }

  /** Retire un écran des récents (masque ses consultations dans le journal). */
  async removeRecent(menuKey) {
    const store = this.getStore()
    store.setRecents(store.recents.filter(r => r.menuKey !== menuKey))
    try {
      await AppActivityService.hideViews(menuKey)
    } catch (error) {
      appConsoleService.error('appMenuShortcutService', error)
    }
  }

  // =========================================================
  // Navigation (mêmes règles que mainLayout.handleMenuItemClick)
  // =========================================================

  async open(item) {
    if (!item?.name) return
    if (item.mode === 'panel') {
      await NavigationService.push({name: item.name, params: {itemId: item.id}})
    } else {
      await NavigationService.push({name: item.name})
    }
  }
}

export default new AppMenuShortcutService();
